import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './IBPopup.css';
import {
  RANGE_OPTIONS,
  periodKpiLabel,
  computePerf,
  hasEnoughHistory,
  filterHistoryToRange,
  computeYDomain,
} from './popupShared';

// Map range label → per-position P&L field for the "Return" column.
// 1W uses WTD snapshot; others accumulate over time.
const RANGE_PNL_FIELD = {
  '1D':  null,
  '1W':  'wtdPnl',
  '1M':  null,
  '3M':  null,
  '6M':  null,
  '1Y':  null,
  'YTD': null,
};

// ─── Formatters ───────────────────────────────────────────────────────────────

function fmtEur(value, showSign = false) {
  if (value == null || isNaN(value)) return '—';
  const sign = showSign ? (value >= 0 ? '+' : '') : '';
  return `${sign}€${Math.round(Math.abs(value)).toLocaleString()}`;
}

function fmtPct(value) {
  if (value == null || isNaN(value)) return '';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function fmtNum(value, decimals = 2) {
  if (value == null || isNaN(value)) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

function pnlClass(value) {
  if (value == null || isNaN(value)) return '';
  return value >= 0 ? 'ib-pos' : 'ib-neg';
}

function formatYAxis(value) {
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000)     return `€${(value / 1_000).toFixed(0)}k`;
  return `€${value}`;
}

function formatXAxis(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ─── Chart tooltip ────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const dateLabel = new Date(label).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  return (
    <div className="ib-chart-tooltip">
      <div className="ib-chart-tooltip-date">{dateLabel}</div>
      <div>€{Math.round(payload[0]?.value ?? 0).toLocaleString()}</div>
    </div>
  );
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'description',      label: 'Instrument',      align: 'left'  },
  { key: 'assetType',        label: 'Type',            align: 'left'  },
  { key: 'amount',           label: 'Qty',             align: 'right' },
  { key: 'currentPrice',     label: 'Price',           align: 'right' },
  { key: 'localExposure',    label: 'Local Exposure',  align: 'right' },
  { key: 'exposureInBase',   label: 'Exposure (EUR)',  align: 'right' },
  { key: 'pnlOnTradeInBase', label: 'P&L',             align: 'right' },
  { key: '__return__',       label: 'Return',          align: 'right' },
];

function getReturnValue(pos, rangeLabel) {
  const field = RANGE_PNL_FIELD[rangeLabel];
  if (!field) return null;
  return pos[field] ?? null;
}

// Group ordering: equities/ETFs/options/futures (0), bonds (1), cash (2), adjustments (3).
// Used as the primary sort key so the breakdown always reads stocks → bonds → cash → adjustments,
// regardless of which column the user is sorting by.
function categoryRank(assetType) {
  const t = (assetType || '').toUpperCase();
  if (t === 'ADJUSTMENT') return 3;
  if (t === 'CASH')       return 2;
  if (t === 'BOND' || t === 'GOVT' || t === 'NOTE' || t === 'FIXED INCOME') return 1;
  return 0;
}

function sortPositions(positions, sortKey, sortDir, rangeLabel) {
  return [...positions].sort((a, b) => {
    // Primary: category (always ascending: stocks → bonds → cash → adjustments)
    const ra = categoryRank(a.assetType);
    const rb = categoryRank(b.assetType);
    if (ra !== rb) return ra - rb;

    // Secondary: user-selected column
    let va = sortKey === '__return__' ? getReturnValue(a, rangeLabel) : a[sortKey];
    let vb = sortKey === '__return__' ? getReturnValue(b, rangeLabel) : b[sortKey];

    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;

    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();

    if (va < vb) return sortDir === 'asc' ? -1 :  1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });
}

// ─── Main component ───────────────────────────────────────────────────────────

function IBPopup({ onClose, data, loading, error }) {
  const [range, setRange]     = useState('1W');
  const [sortKey, setSortKey] = useState('pnlOnTradeInBase');
  const [sortDir, setSortDir] = useState('desc');

  const valueHistory = useMemo(
    () => data?.account?.valueHistory || [],
    [data]
  );
  const selectedOption = useMemo(
    () => RANGE_OPTIONS.find(r => r.label === range) || RANGE_OPTIONS[1],
    [range]
  );

  const chartData = useMemo(
    () => filterHistoryToRange(valueHistory, selectedOption),
    [valueHistory, selectedOption]
  );
  const yDomain = useMemo(() => computeYDomain(chartData), [chartData]);

  const periodPerf = useMemo(
    () => computePerf(valueHistory, selectedOption),
    [valueHistory, selectedOption]
  );

  // Sorted positions
  const positions = useMemo(() => {
    const raw = data?.positions || [];
    return sortPositions(raw, sortKey, sortDir, range);
  }, [data, sortKey, sortDir, range]);

  const account = data?.account || {};

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortIcon = (key) => {
    if (key !== sortKey) return <span className="ib-sort-icon">⇅</span>;
    return <span className="ib-sort-icon">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  // Totals row
  const totals = useMemo(() => {
    if (!positions.length) return null;
    const sumExp    = positions.reduce((s, p) => s + (p.exposureInBase  ?? 0), 0);
    const sumPnl    = positions.reduce((s, p) => s + (p.pnlOnTradeInBase ?? 0), 0);
    const returnPnls = positions.map(p => getReturnValue(p, range)).filter(v => v != null);
    const sumReturn  = returnPnls.length ? returnPnls.reduce((s, v) => s + v, 0) : null;
    return { sumExp, sumPnl, sumReturn };
  }, [positions, range]);

  // Format asOf date for footnote
  const asOfLabel = account.asOf
    ? new Date(account.asOf).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  // ─── Render states ──────────────────────────────────────────────────────────
  const renderBody = () => {
    if (loading) return <div className="ib-loading">Loading portfolio…</div>;
    if (error)   return (
      <div className="ib-error"><p>⚠ {error}</p><button onClick={onClose}>Close</button></div>
    );

    const returnLabel = RANGE_PNL_FIELD[range] ? `Return (${range})` : `Return (${range}) ¹`;

    return (
      <>
        {/* ── Account summary bar ── */}
        <div className="ib-account-bar">
          <div className="ib-account-total">
            <span className="ib-label">Portfolio Value</span>
            <span className="ib-big-value">
              €{Math.round(account.totalEurValue || 0).toLocaleString()}
            </span>
          </div>
          <div className="ib-account-stat">
            <span className="ib-label">Today</span>
            <span className={`ib-pnl ${pnlClass(account.dailyPnl)}`}>
              {fmtEur(account.dailyPnl, true)}
              {account.dailyPnlPercent != null && (
                <span className="ib-pct"> ({fmtPct(account.dailyPnlPercent)})</span>
              )}
            </span>
          </div>
          <div className="ib-account-stat">
            <span className="ib-label">{periodKpiLabel(selectedOption)}</span>
            <span className={`ib-pnl ${pnlClass(periodPerf?.abs)}`}>
              {periodPerf ? fmtEur(periodPerf.abs, true) : '—'}
              {periodPerf?.pct != null && (
                <span className="ib-pct"> ({fmtPct(periodPerf.pct)})</span>
              )}
            </span>
          </div>
        </div>

        {/* ── Performance chart ── */}
        <div className="ib-chart-section">
          <div className="ib-chart-header">
            <span className="ib-section-title">Account Performance</span>
            <div className="ib-range-buttons">
              {RANGE_OPTIONS.map(r => {
                const enabled = hasEnoughHistory(valueHistory, r);
                return (
                  <button
                    key={r.label}
                    className={`ib-range-btn ${range === r.label ? 'active' : ''}`}
                    onClick={() => enabled && setRange(r.label)}
                    disabled={!enabled}
                    title={enabled ? '' : 'Not enough history for this range yet'}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
          {chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 11 }} minTickGap={40} />
                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} width={72} domain={yDomain} allowDataOverflow={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="eurValue" stroke="#EF4444" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="ib-chart-empty">Not enough history for this range yet.</div>
          )}
        </div>

        {/* ── Positions table ── */}
        <div className="ib-positions-section">
          <div className="ib-section-title">Positions ({positions.length})</div>
          {positions.length === 0 ? (
            <div className="ib-chart-empty">No open positions found.</div>
          ) : (
            <div className="ib-table-wrapper">
              <table className="ib-table">
                <thead>
                  <tr>
                    {COLUMNS.map(col => (
                      <th
                        key={col.key}
                        className={`${col.align === 'right' ? 'ib-num' : ''} ib-th-sortable`}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.key === '__return__' ? returnLabel : col.label}
                        {sortIcon(col.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {positions.map((pos, i) => {
                    const returnVal = getReturnValue(pos, range);
                    return (
                      <tr key={pos.positionId || i}>
                        {/* Instrument */}
                        <td className="ib-desc" title={pos.description}>{pos.description || '—'}</td>

                        {/* Type */}
                        <td><span className="ib-type-badge">{pos.assetType || '—'}</span></td>

                        {/* Qty */}
                        <td className="ib-num">{fmtNum(pos.amount, 0)}</td>

                        {/* Price */}
                        <td className="ib-num">
                          {pos.localCurrency} {fmtNum(pos.currentPrice, 2)}
                        </td>

                        {/* Local Exposure */}
                        <td className="ib-num">
                          {pos.localCurrency} {fmtNum(pos.localExposure, 0)}
                        </td>

                        {/* Exposure EUR */}
                        <td className="ib-num">
                          €{fmtNum(pos.exposureInBase, 0)}
                        </td>

                        {/* P&L */}
                        <td className={`ib-num ${pnlClass(pos.pnlOnTradeInBase)}`}>
                          <div>{fmtEur(pos.pnlOnTradeInBase, true)}</div>
                          {pos.pnlOnTradePercent != null && (
                            <div className="ib-pct">{fmtPct(pos.pnlOnTradePercent)}</div>
                          )}
                        </td>

                        {/* Return (range-tied) */}
                        <td className={`ib-num ${pnlClass(returnVal)}`}>
                          {returnVal != null ? fmtEur(returnVal, true) : '—'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* ── Totals row ── */}
                  {totals && (
                    <tr className="ib-totals-row">
                      <td colSpan={4}><strong>TOTAL</strong></td>
                      <td className="ib-num">—</td>
                      <td className="ib-num"><strong>€{fmtNum(totals.sumExp, 0)}</strong></td>
                      <td className={`ib-num ${pnlClass(totals.sumPnl)}`}>
                        <strong>{fmtEur(totals.sumPnl, true)}</strong>
                      </td>
                      <td className={`ib-num ${pnlClass(totals.sumReturn)}`}>
                        <strong>{totals.sumReturn != null ? fmtEur(totals.sumReturn, true) : '—'}</strong>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {!RANGE_PNL_FIELD[range] && (
            <p className="ib-footnote">
              ¹ Per-position {range} data builds up from daily snapshots — available after sufficient history is accumulated.
            </p>
          )}
          {asOfLabel && (
            <p className="ib-footnote">
              Prices as of last market close ({asOfLabel}) — IBKR Flex Web Service
            </p>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content ib-popup-content" onClick={e => e.stopPropagation()}>
        <div className="ib-popup-header">
          <h2>Interactive Brokers — Portfolio</h2>
          <button className="ib-close-btn" onClick={onClose}>✕</button>
        </div>
        {renderBody()}
      </div>
    </div>
  );
}

export default IBPopup;
